import { Edge, Node } from "@xyflow/react"
import { CARD_HEIGHT, CARD_HORIZONTAL_SPACING, CARD_SPACING, CARD_WIDTH } from "./constants"

// One row = a card plus the vertical gap below it. Y is derived purely from tree depth, so a node's
// Y is canonical (depth-based) regardless of its incoming position. This keeps re-layouts
// idempotent — any per-workflow vertical offset applied afterwards never compounds.
const ROW_HEIGHT = CARD_HEIGHT + CARD_SPACING

/**
 * Hello traveler! This was all AI generated, no kidding. The model that worked smoothly was
 * Gemini 2.5 Pro, after testing some other ones.
 */

/**
 * Repositions nodes in the graph, ensuring children are aligned under their specific parents.
 * This version uses a recursive approach to calculate subtree widths and positions nodes accordingly.
 */
function repositionNodes(originalNodes: Node[], allEdges: Edge[]): Node[] {
  if (originalNodes.length === 0) return []

  const newNodes = originalNodes.map((n) => ({ ...n, position: { ...n.position } }))
  const nodeMap = new Map(newNodes.map((n) => [n.id, n]))

  const childrenMap = new Map<string, string[]>()
  const parentMap = new Map<string, string>()
  newNodes.forEach((n) => childrenMap.set(n.id, []))

  const branchByChild = new Map(allEdges.map((edge) => [edge.target, edge.sourceHandle]))

  allEdges.forEach((edge) => {
    if (nodeMap.has(edge.source) && nodeMap.has(edge.target)) {
      childrenMap.get(edge.source)?.push(edge.target)
      parentMap.set(edge.target, edge.source)
    }
  })

  // Sort children by ID for stable ordering.
  for (const parentId of childrenMap.keys()) {
    const children = childrenMap.get(parentId)
    if (children) {
      children.sort((aId, bId) => {
        const aBranch = branchByChild.get(aId) === "Fail" ? 1 : 0
        const bBranch = branchByChild.get(bId) === "Fail" ? 1 : 0
        return aBranch - bBranch || aId.localeCompare(bId)
      })
    }
  }

  const subtreeWidths = new Map<string, number>()

  function childrenWidth(children: string[]) {
    return (
      children.reduce((width, childId) => width + (subtreeWidths.get(childId) ?? CARD_WIDTH), 0) +
      Math.max(0, children.length - 1) * CARD_HORIZONTAL_SPACING
    )
  }

  function passingChildren(children: string[]) {
    return children.filter((childId) => branchByChild.get(childId) !== "Fail")
  }

  function failingChildren(children: string[]) {
    return children.filter((childId) => branchByChild.get(childId) === "Fail")
  }

  function calculateSubtreeWidth(nodeId: string): number {
    if (subtreeWidths.has(nodeId)) {
      return subtreeWidths.get(nodeId)!
    }

    const children = childrenMap.get(nodeId) || []
    if (children.length === 0) {
      subtreeWidths.set(nodeId, CARD_WIDTH)
      return CARD_WIDTH
    }

    for (const childId of children) {
      calculateSubtreeWidth(childId)
    }

    const isCondition = nodeMap.get(nodeId)?.type === "condition"
    const passWidth = Math.max(CARD_WIDTH, childrenWidth(passingChildren(children)))
    const fail = failingChildren(children)
    const width = isCondition
      ? passWidth + (fail.length > 0 ? CARD_HORIZONTAL_SPACING + childrenWidth(fail) : 0)
      : childrenWidth(children)
    subtreeWidths.set(nodeId, Math.max(CARD_WIDTH, width)) // Node itself also has a width
    return subtreeWidths.get(nodeId)!
  }

  // Calculate subtree widths for all nodes
  newNodes.forEach((node) => {
    if (!parentMap.has(node.id) || !nodeMap.has(parentMap.get(node.id)!)) {
      calculateSubtreeWidth(node.id)
    }
  })
  newNodes.forEach((node) => {
    if (!subtreeWidths.has(node.id)) {
      calculateSubtreeWidth(node.id)
    }
  })

  function positionNodesRecursive(nodeId: string, currentXOffset: number, depth: number) {
    const node = nodeMap.get(nodeId)
    if (!node) return

    const children = childrenMap.get(nodeId) || []
    const isCondition = node.type === "condition"
    const passWidth = Math.max(CARD_WIDTH, childrenWidth(passingChildren(children)))

    let childX = currentXOffset
    for (const childId of children) {
      if (nodeMap.has(childId)) {
        if (
          isCondition &&
          branchByChild.get(childId) === "Fail" &&
          childX < currentXOffset + passWidth
        ) {
          childX = currentXOffset + passWidth + CARD_HORIZONTAL_SPACING
        }
        positionNodesRecursive(childId, childX, depth + 1)
        childX += (subtreeWidths.get(childId) || CARD_WIDTH) + CARD_HORIZONTAL_SPACING
      }
    }

    const alignedChildren = isCondition ? passingChildren(children) : children
    const firstChildId = alignedChildren[0]
    const lastChildId = alignedChildren.at(-1)
    const firstChild = firstChildId ? nodeMap.get(firstChildId) : undefined
    const lastChild = lastChildId ? nodeMap.get(lastChildId) : undefined
    const x =
      firstChild && lastChild
        ? (firstChild.position.x + lastChild.position.x) / 2
        : currentXOffset + CARD_WIDTH / 2
    node.position = { x, y: depth * ROW_HEIGHT }
  }

  const rootNodes = newNodes.filter(
    (n) => !parentMap.has(n.id) || !nodeMap.has(parentMap.get(n.id)!),
  )

  // Capture each root's insertion order (its index in the node array) before sorting. A newly added
  // event is appended last, so this is what keeps it at the right edge of its workflow.
  const rootOrder = new Map(rootNodes.map((node, index) => [node.id, index]))

  const workflowIdOf = (node: Node) =>
    String((node.data as { workflowId?: string } | undefined)?.workflowId ?? "")

  // Order each workflow by when its first event was added (its earliest root insertion index). A
  // freshly added workflow's first event is appended last, so this keeps the new workflow at the
  // right edge of the canvas — mirroring how a new event lands at the right edge of its workflow.
  const workflowOrder = new Map<string, number>()
  for (const root of rootNodes) {
    const workflowId = workflowIdOf(root)
    const order = rootOrder.get(root.id)!
    if (!workflowOrder.has(workflowId) || order < workflowOrder.get(workflowId)!) {
      workflowOrder.set(workflowId, order)
    }
  }

  // Cluster roots by their workflow group (so one workflow's events stay contiguous and can be
  // wrapped by a single box), ordering workflows by insertion order and, within a workflow, the
  // events by insertion order too — so both a freshly added workflow and a freshly added event land
  // to the right of the existing ones. We deliberately do NOT order by pixel position: a disabled
  // workflow shifts its cards down to make room for the banner, which would otherwise sort an
  // un-shifted new event ahead of the existing ones and place it on the left.
  rootNodes.sort((a, b) => {
    const aWorkflow = workflowIdOf(a)
    const bWorkflow = workflowIdOf(b)
    if (aWorkflow !== bWorkflow) {
      return workflowOrder.get(aWorkflow)! - workflowOrder.get(bWorkflow)!
    }
    return rootOrder.get(a.id)! - rootOrder.get(b.id)!
  })

  let totalRootsWidth = 0
  if (rootNodes.length > 0) {
    totalRootsWidth =
      rootNodes.reduce((sum, root) => {
        return sum + (subtreeWidths.get(root.id) || CARD_WIDTH)
      }, 0) +
      Math.max(0, rootNodes.length - 1) * CARD_HORIZONTAL_SPACING
  }

  let currentGlobalX = -totalRootsWidth / 2

  for (const rootNode of rootNodes) {
    positionNodesRecursive(rootNode.id, currentGlobalX, 0)
    currentGlobalX += (subtreeWidths.get(rootNode.id) || CARD_WIDTH) + CARD_HORIZONTAL_SPACING
  }

  return newNodes
}

// Extra horizontal gap inserted between workflow clusters, on top of the tight in-workflow spacing,
// so each workflow's box stays clearly separated from the next.
const WORKFLOW_CLUSTER_GAP = 120

// Pushes each workflow cluster apart so events inside a workflow can sit close together while the
// workflows themselves remain visually separated.
function separateWorkflowClusters(nodes: Node[]): Node[] {
  const byWorkflow = new Map<string, Node[]>()
  for (const node of nodes) {
    const workflowId = String((node.data as { workflowId?: string } | undefined)?.workflowId ?? "")
    const group = byWorkflow.get(workflowId) ?? []
    group.push(node)
    byWorkflow.set(workflowId, group)
  }

  const clusters = [...byWorkflow.values()]
    .map((group) => ({ group, minX: Math.min(...group.map((n) => n.position.x)) }))
    .sort((a, b) => a.minX - b.minX)

  return clusters.flatMap(({ group }, index) =>
    group.map((node) => ({
      ...node,
      position: { ...node.position, x: node.position.x + index * WORKFLOW_CLUSTER_GAP },
    })),
  )
}

// Lays out the tree, then separates the per-workflow clusters.
export function layoutNodes(nodes: Node[], edges: Edge[]): Node[] {
  return separateWorkflowClusters(repositionNodes(nodes, edges))
}
